import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ERR, clearReauthToken, get, onSessionLost, post, setReauthResolver, setReauthToken } from "./adminApi";

/* Contexte de la console : administrateur courant, permissions, ré-auth,
   emprunt d'identité. Aucun écran ne lit les permissions autrement que par
   `can()` — c'est la seule source de vérité côté client.

   Rappel important : cette garde est du confort d'interface. Masquer une entrée
   n'est pas une autorisation. Chaque appel est ré-autorisé par le serveur, qui
   reste seul juge : un onglet ouvert, un lien collé ou un appel direct à l'API
   doivent échouer côté serveur même si l'interface a laissé passer. */

const FALLBACK = {
  admin: null,
  permissions: [],
  roles: [],
  can: () => false,
  loading: false,
  ready: false,
  error: null,
  impersonation: null,
  refresh: () => Promise.resolve(null),
  reauth: () => Promise.reject(new Error("Contexte d'administration absent.")),
  requestReauth: () => Promise.resolve(null),
  reauthRequest: null,
  resolveReauth: () => {},
  cancelReauth: () => {},
  stopImpersonation: () => Promise.resolve(),
};

// Durée de validité par défaut d'une ré-authentification (docs/rbac.md § 5).
const REAUTH_TTL = 600;

const AdminCtx = createContext(FALLBACK);

// "users.*" couvre "users.read" ; "*" couvre tout (super-administrateur).
function grants(granted, wanted) {
  if (granted === "*" || granted === wanted) return true;
  return granted.endsWith(".*") && wanted.startsWith(granted.slice(0, -1));
}

export function AdminProvider({ children, session = null, autoload = true, overrides = {} }) {
  const [state, setState] = useState(() => ({
    admin: session?.admin ?? null,
    permissions: session?.permissions ?? [],
    roles: session?.roles ?? [],
    impersonation: session?.impersonation ?? null,
    loading: autoload && !session,
    ready: Boolean(session),
    error: null,
  }));
  const [reauthRequest, setReauthRequest] = useState(null);
  const pending = useRef(null);
  const injected = useRef(overrides);
  injected.current = overrides;

  const refresh = useCallback(async () => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const fetcher = injected.current.fetchSession || (() => get("/session"));
      const data = await fetcher();
      setState({
        admin: data.admin ?? null,
        permissions: data.permissions ?? [],
        roles: data.roles ?? [],
        impersonation: data.impersonation ?? null,
        loading: false,
        ready: true,
        error: null,
      });
      return data;
    } catch (err) {
      setState(s => ({ ...s, loading: false, ready: true, error: err }));
      return null;
    }
  }, []);

  useEffect(() => {
    if (session) {
      setState({
        admin: session.admin ?? null,
        permissions: session.permissions ?? [],
        roles: session.roles ?? [],
        impersonation: session.impersonation ?? null,
        loading: false,
        ready: true,
        error: null,
      });
      return;
    }
    if (autoload) refresh();
  }, [session, autoload, refresh]);

  // Une session perdue vide l'état : plus aucune permission, donc plus aucune
  // action visible, en attendant la redirection vers l'écran de connexion.
  useEffect(() => onSessionLost(err => {
    setState(s => ({ ...s, admin: null, permissions: [], impersonation: null, error: err, ready: true, loading: false }));
  }), []);

  const permissions = state.permissions;
  const can = useCallback(perm => {
    if (!perm) return true;
    const wanted = Array.isArray(perm) ? perm : [perm];
    return wanted.every(p => permissions.some(granted => grants(granted, p)));
  }, [permissions]);

  /* Ré-authentification. Perform n'a pas de mot de passe : la preuve d'identité
     est un nouveau lien envoyé par e-mail. L'appel se fait donc en deux temps —
     d'abord le motif (obligatoire, journalisé), qui déclenche l'envoi du lien,
     puis le code reçu. Le jeton obtenu ne vaut que pour l'action demandée. */
  const reauth = useCallback(async input => {
    const payload = typeof input === "string" ? { code: input } : (input || {});
    const call = injected.current.reauth || (body => post("/reauth", body));
    const data = await call(payload);
    if (data?.sent || data?.status === "sent") {
      return { status: "sent", email: data.email ?? null };
    }
    const token = typeof data === "string" ? data : data?.token;
    if (!token) throw new Error("Ré-authentification refusée.");
    setReauthToken(token, data?.expires_in ?? REAUTH_TTL);
    return { token, expires_in: data?.expires_in ?? REAUTH_TTL, reason: payload.reason ?? null };
  }, []);

  // Ouvre le dialogue global de ré-auth et résout avec le jeton obtenu.
  const requestReauth = useCallback(error => new Promise(resolve => {
    pending.current = resolve;
    setReauthRequest({ error: error ?? null, at: Date.now() });
  }), []);

  const resolveReauth = useCallback(granted => {
    setReauthRequest(null);
    const resolve = pending.current;
    pending.current = null;
    if (resolve) resolve(granted);
  }, []);

  const cancelReauth = useCallback(() => resolveReauth(null), [resolveReauth]);

  useEffect(() => {
    setReauthResolver(requestReauth);
    return () => setReauthResolver(null);
  }, [requestReauth]);

  const stopImpersonation = useCallback(async () => {
    const call = injected.current.stopImpersonation || (() => post("/impersonation/stop"));
    try {
      await call();
    } finally {
      clearReauthToken();
      setState(s => ({ ...s, impersonation: null }));
    }
  }, []);

  const value = useMemo(() => ({
    admin: state.admin,
    permissions: state.permissions,
    roles: state.roles,
    loading: state.loading,
    ready: state.ready,
    error: state.error,
    impersonation: state.impersonation,
    can,
    refresh,
    reauth,
    requestReauth,
    reauthRequest,
    resolveReauth,
    cancelReauth,
    stopImpersonation,
  }), [state, can, refresh, reauth, requestReauth, reauthRequest, resolveReauth, cancelReauth, stopImpersonation]);

  return <AdminCtx.Provider value={value}>{children}</AdminCtx.Provider>;
}

export function useAdmin() {
  return useContext(AdminCtx);
}

// Sucre pour les cas où seul le booléen compte.
export function useCan(perm) {
  return useAdmin().can(perm);
}

export const SESSION_LOST = ERR.SESSION;
